import { useEffect, useState } from "react";
import type { QuestionView } from "../api/types.js";
import { isNumericAnswer } from "../lib/numericAnswer.js";

export interface AnswerSubmission {
  answer?: string;
  selectedChoiceIds?: string[];
}

interface Props {
  question: QuestionView;
  submitting: boolean;
  /** クールダウンの残り秒数（0 なら送信可能） */
  cooldownSec: number;
  disabled: boolean;
  onSubmit: (submission: AnswerSubmission) => void;
}

/** 解答形式に応じた入力UI */
export function AnswerForm({
  question,
  submitting,
  cooldownSec,
  disabled,
  onSubmit,
}: Props): React.ReactElement {
  const [text, setText] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  // 問題が切り替わったら入力を初期化する
  useEffect(() => {
    setText("");
    setSelected([]);
  }, [question.id]);

  const isChoice =
    question.answerType === "choice_single" || question.answerType === "choice_multi";
  const hasInput = isChoice ? selected.length > 0 : text.trim() !== "";
  const blocked = disabled || submitting || cooldownSec > 0;

  /**
   * 数値問題で、数値として読めない入力を送信前に止める。
   *
   * そのまま送るとサーバーは誤答として扱い、減点とクールダウンが確定する。
   * 「単位まで入れてしまった」「変換で全角の記号が混ざった」程度のことで
   * 点を失うのは納得しにくい。ここで止めれば減点そのものが起きない。
   */
  const needsNumber = question.answerType === "number";
  const numberInvalid = needsNumber && hasInput && !isNumericAnswer(text);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (blocked || !hasInput || numberInvalid) return;
    onSubmit(isChoice ? { selectedChoiceIds: selected } : { answer: text });
  };

  const toggleChoice = (choiceId: string): void => {
    if (question.answerType === "choice_single") {
      setSelected([choiceId]);
      return;
    }
    setSelected((prev) =>
      prev.includes(choiceId) ? prev.filter((id) => id !== choiceId) : [...prev, choiceId]
    );
  };

  return (
    <form className="answer-form" onSubmit={handleSubmit}>
      {question.answerType === "text" && (
        <input
          type="text"
          className="answer-form__input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="解答を入力"
          disabled={blocked}
          // iOS の自動変換・自動大文字化を切る（解答が勝手に変わるのを防ぐ）
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="send"
          aria-label={`${question.label}の解答`}
        />
      )}

      {question.answerType === "number" && (
        <input
          type="text"
          // type="number" はスピナーや入力制限で扱いにくいため、
          // 数値キーボードだけを inputMode で指定する
          inputMode="decimal"
          className="answer-form__input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="数値を入力"
          disabled={blocked}
          enterKeyHint="send"
          aria-label={`${question.label}の解答`}
          aria-invalid={numberInvalid}
          aria-describedby={numberInvalid ? `number-hint-${question.id}` : undefined}
        />
      )}

      {numberInvalid && (
        <p className="answer-form__warn" id={`number-hint-${question.id}`} role="status">
          数字で入力してください。単位や記号は不要です。
        </p>
      )}

      {isChoice && (
        <div
          className="choices"
          role={question.answerType === "choice_single" ? "radiogroup" : "group"}
          aria-label={`${question.label}の選択肢`}
        >
          {question.choices.map((choice) => {
            const checked = selected.includes(choice.id);
            return (
              <button
                key={choice.id}
                type="button"
                className={`choice ${checked ? "choice--selected" : ""}`}
                onClick={() => toggleChoice(choice.id)}
                disabled={blocked}
                role={question.answerType === "choice_single" ? "radio" : "checkbox"}
                aria-checked={checked}
              >
                <span className="choice__marker" aria-hidden="true">
                  {checked ? "✓" : ""}
                </span>
                <span className="choice__label">{choice.label}</span>
              </button>
            );
          })}
          {question.answerType === "choice_multi" && (
            <p className="choices__note">当てはまるものをすべて選んでください</p>
          )}
        </div>
      )}

      <button
        type="submit"
        className="btn btn--primary btn--block"
        disabled={blocked || !hasInput || numberInvalid}
      >
        {submitting
          ? "送信中…"
          : cooldownSec > 0
            ? `あと ${cooldownSec} 秒お待ちください`
            : "解答する"}
      </button>
    </form>
  );
}
